import { useToggle } from "client/hooks/utils/useToggle";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { Popup } from "components/common/Popup/Popup";
import { Translate } from "components/common/Translate/Translate";
import type { CompressImage, CompressState } from "store/features/compress/types";
import { MaxFileSizeConfirmationModal } from "./MaxFileSizeConfirmationModal/MaxFileSizeConfirmationModal";
import { useApplyButtons } from "./useApplyButtons";

export interface ApplyButtonsProps {
    image: CompressImage;
    type: CompressState["compressionSettings"]["type"];
}

export const ApplyButtons = ({ image, type }: ApplyButtonsProps) => {
    const {
        handleApply,
        handleApplyToAll,
        isApplyToAllDisabled,
        isMaxFileSizeConfirmationModalOpen,
        setIsMaxFileSizeConfirmationModalOpen,
        handleReset,
    } = useApplyButtons();
    const { isOpen, onClose, toggle } = useToggle();
    return (
        <div className="flex gap-4 pb-2 md:pb-0">
            <Popup
                isOpen={isOpen}
                close={onClose}
                content={
                    <div className="absolute top-full right-0 pt-2">
                        <div
                            className="bg-white dark:bg-darkSurface-200 border border-irGray-200 dark:border-darkSurface-600 rounded-md text-center shadow-lg text-black dark:text-white overflow-hidden"
                            onClick={onClose}
                        >
                            <button
                                className="w-full hover:bg-irGray-200 dark:hover:bg-darkSurface-300 disabled:bg-irGray-100 dark:disabled:bg-darkSurface-400 disabled:text-irGray-600 overflow-hidden whitespace-nowrap px-12 py-2 border-b border-b-irGray-200 dark:border-b-darkSurface-600"
                                onClick={() => handleApplyToAll(image)}
                                disabled={isApplyToAllDisabled}
                                data-testid={`apply-to-all-button`}
                            >
                                <Translate keyName="apply-to-all-button-label" />
                            </button>
                            <button
                                className="w-full overflow-hidden whitespace-nowrap px-12 py-2 capitalize hover:bg-irGray-200 dark:hover:bg-darkSurface-300"
                                onClick={handleReset}
                                data-testid={`reset-button`}
                            >
                                <Translate keyName="reset" />
                            </button>
                        </div>
                    </div>
                }
            >
                <div className="flex items-stretch bg-irBlue-200 text-white rounded-md overflow-hidden">
                    <button
                        className="border-r border-r-white dark:border-r-darkSurface-300 px-6 py-2 hover:bg-irBlue-400 isabled:bg-irGray-500"
                        onClick={() => type === "max_file_size" && handleApply(image)}
                        disabled={image.state === "compressing"}
                        data-testid={`apply-button`}
                    >
                        <Translate keyName="apply-button-label" />
                    </button>
                    <button
                        className={`hover:bg-irBlue-400 disabled:bg-irGray-500 px-2 grid place-items-center ${
                            isOpen ? "rotate-180" : ""
                        }`}
                        onClick={toggle}
                        disabled={image.state === "compressing"}
                        data-testid={`drop-down-arrow`}
                    >
                        <ArrowIcon className="stroke-white" />
                    </button>
                </div>
            </Popup>
            {/**
            
            <button
                className="bg-irBlue-200 dark:bg-darkPrimary-100 dark:hover:bg-darkPrimary-200 hover:bg-irBlue-400 text-white py-2 px-4 rounded-md disabled:bg-irGray-400"
                disabled={image.state === "compressing"}
                onClick={() => type === "max_file_size" && handleApply(image)}
            >
                <Translate keyName="apply-button-label" />
            </button>
            <button
                className="bg-irBlue-200 dark:bg-darkPrimary-100 dark:hover:bg-darkPrimary-200 hover:bg-irBlue-400 text-white py-2 px-4 whitespace-nowrap rounded-md disabled:bg-irGray-400"
                disabled={isApplyToAllDisabled}
                onClick={() => handleApplyToAll(image)}
            >
                <Translate keyName="apply-to-all-button-label" />
            </button>
            */}
            <MaxFileSizeConfirmationModal
                isOpen={isMaxFileSizeConfirmationModalOpen}
                close={() => setIsMaxFileSizeConfirmationModalOpen(false)}
                callback={() => {
                    handleApplyToAll(image);
                    setIsMaxFileSizeConfirmationModalOpen(false);
                }}
            />
        </div>
    );
};
